import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, AlertTriangle, Shield, Zap } from "lucide-react";
import { EvidenceTooltip } from "./EvidenceTooltip";

interface OperationalRisk {
  risk: string;
  metric?: string;
  trigger?: string;
  mitigation?: string;
}

interface DominantStrategySectionProps {
  data: {
    recommended_option_label?: string;
    rationale?: string[];
    risks?: string[] | OperationalRisk[];
    risk_mitigation?: string[];
    decision_rationale?: string;
  };
  isConstraintBreak?: boolean;
  isStrategyShift?: boolean;
  evidence?: { source_refs?: string[]; data_ids?: Record<string, any> };
}

export function DominantStrategySection({ data, isConstraintBreak, isStrategyShift, evidence }: DominantStrategySectionProps) {
  if (!data) return null;

  const rationale = data.rationale || [];
  const risks = data.risks || [];
  const mitigation = data.risk_mitigation || [];

  return (
    <Card id="dominant-strategy" className="border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-transparent scroll-mt-24">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <EvidenceTooltip evidence={evidence}>Dominante Strategie</EvidenceTooltip>
          </CardTitle>
          {data.recommended_option_label && (
            <Badge variant="default" className="text-sm px-3">
              {data.recommended_option_label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Constraint Warnings - PROMINENT */}
        {(isConstraintBreak || isStrategyShift) && (
          <div className={`rounded-lg p-3 border ${isConstraintBreak ? 'bg-destructive/10 border-destructive/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
            <div className="flex items-start gap-2">
              {isConstraintBreak ? (
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              ) : (
                <Zap className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              )}
              <div>
                <p className={`text-sm font-semibold ${isConstraintBreak ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'}`}>
                  {isConstraintBreak ? 'Constraint-Bruch' : 'Strategiewechsel'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isConstraintBreak
                    ? 'Diese Option bricht die in Phase 1 definierten Constraints. Besondere Begründung erforderlich.'
                    : 'Diese Option weicht signifikant vom Phase-1-Commitment ab. Review empfohlen.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Decision Rationale */}
        {data.decision_rationale && (
          <div className="system-derived bg-primary/10 rounded-lg p-4 border border-primary/20">
            <p className="text-sm font-medium leading-relaxed break-words">{data.decision_rationale}</p>
          </div>
        )}

        {/* Max 3 harte Gründe */}
        {rationale.length > 0 && (
          <div className="system-derived-compact">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Deshalb diese Option:</p>
            <ul className="space-y-1.5">
              {rationale.slice(0, 3).map((point, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary font-bold">{index + 1}.</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risks & Mitigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {risks.length > 0 && (
            <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h4 className="font-semibold text-xs text-amber-700 dark:text-amber-400 uppercase">Risiken</h4>
              </div>
              <ul className="space-y-2">
                {(risks as any[]).slice(0, 2).map((risk, index) => {
                  const riskText = typeof risk === 'string' ? risk : risk.risk;
                  const riskTrigger = typeof risk === 'object' ? risk.trigger : null;
                  return (
                    <li key={index} className="text-xs">
                      <span className="text-muted-foreground">• {riskText}</span>
                      {riskTrigger && (
                        <span className="block text-amber-600 dark:text-amber-400 mt-0.5 ml-2">
                          → Trigger: {riskTrigger}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {mitigation.length > 0 && (
            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-green-500" />
                <h4 className="font-semibold text-xs text-green-700 dark:text-green-400 uppercase">Absicherung</h4>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {mitigation.slice(0, 2).map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}